import React, { useState, useEffect, useMemo, useContext } from "react";
import { Container, Badge, Row, Col, Card, CardBody, Button, Progress } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import styles from "../css/ambulatoryVisit.module.scss";
import CommonSearch from "Components/Common/CommonSearch";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import { useAmbulatoryVisitService } from "../api/useAmbulatoryVisitQuery";
import AssignSubjectsModal from "../components/AssignSubjectsModal";
import ViewActivitiesModal from "../components/ViewActivitiesModal";
import { formatDate, GetUserData, showToastMessage } from "common/service/CommonService";
import FullPageLoader from "Components/Common/FullPageLoader";
import { BreadcrumbContext } from "Layouts/Header";
import { subjectColumns } from "../helper/ambulatoryvisitconstants";
import StartVisitModal from "../components/StartVisitModal";
import { usePermission } from "context/PermissionContext";
import AmbulatoryCheckInModal from "../components/AmbulatoryCheckInModal";
import CompleteVisitModal from "../components/CompleteVisitModal";
import Commonbadge from "Components/Common/Commonbadge";
import TruncatedText from "Components/Common/TruncatedText";
import CommonButton from "Components/Common/CommonButton";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";

const AmbulatoryVisitDetail: React.FC = () => {
  const navigate = useNavigate();
  const { visitId } = useParams<{ visitId: string }>();
  const { hasPermission } = usePermission();
  const breadcrumbContext = useContext(BreadcrumbContext);
  const setBreadcrumbItems = breadcrumbContext?.setBreadcrumbItems;

  useEffect(() => {
    setBreadcrumbItems?.([
      { text: "Ambulatory Visits", link: "/study/detail/ambulatory" },
      { text: "Visit Details" },
    ]);
    return () => setBreadcrumbItems?.([]);
  }, [setBreadcrumbItems]);

  const [visit, setVisit] = useState<any>(null);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [assignOpen, setAssignOpen] = useState(false);
  const [visitToAssign, setVisitToAssign] = useState<any>(null);
  const [allActivitiesOpen, setAllActivitiesOpen] = useState(false);
  const [startVisitOpen, setStartVisitOpen] = useState(false);
  const [completeVisitOpen, setCompleteVisitOpen] = useState(false);

  const handleAssignSubjectsClick = (v: any) => {
    setVisitToAssign(v);
    setAssignOpen(true);
  };

  const onSearch = (value: string | null) => {
    setSearchText(value ?? "");
    setCurrentPage(1);
  };

  const studyid = Number(localStorage.getItem("activestudyid") || 1);
  const { 
    SelectdataAmbulatoryVisit, 
    SelectAllAssignedSubjects, 
    insertAmbulatoryVisitStatusHistory,
    checkInSubject,
    checkOutSubject
  } = useAmbulatoryVisitService({
    studyid,
    visitid: Number(visitId),
    searchtext: searchText,
    currentPage: currentPage,
    pageSize: 10,
  });

  useEffect(() => {
    if (SelectdataAmbulatoryVisit.data) {
      setVisit((SelectdataAmbulatoryVisit.data as any)?.data);
    }
  }, [SelectdataAmbulatoryVisit.data]);

  const visitData = useMemo(() => {
    return visit?.visit?.[0] || null;
  }, [visit]);

  const activitiesData = useMemo(() => {
    return visit?.activities || [];
  }, [visit]);

  const [checkInOpen, setCheckInOpen] = useState(false);
  const [activeCheckInSubject, setActiveCheckInSubject] = useState<any>(null);

  const assignedSubjects = useMemo(() => {
    return (SelectAllAssignedSubjects.data as any)?.data || [];
  }, [SelectAllAssignedSubjects.data]);

  const totalRecords = useMemo(() => {
    return (assignedSubjects[0]?.totalcount) || (SelectAllAssignedSubjects?.data as any)?.totalrecords || assignedSubjects.length || 0;
  }, [assignedSubjects, SelectAllAssignedSubjects?.data]);

  const completedSubjectsCount = useMemo(() => {
    return assignedSubjects.filter(
      (s: any) => s.subjectvisitstatus === "checked out" || s.subjectvisitstatus === "completed" || s.checkoutdate || s.checkoutDate
    ).length;
  }, [assignedSubjects]);

  const checkedInCount = useMemo(() => {
    return assignedSubjects.filter(
      (s: any) => s.subjectvisitstatus === "checked in" || s.subjectvisitstatus === "checked out" || s.checkindate || s.checkinDate
    ).length;
  }, [assignedSubjects]);

  const checkedOutCount = useMemo(() => {
    return assignedSubjects.filter(
      (s: any) => s.subjectvisitstatus === "checked out" || s.subjectvisitstatus === "completed" || s.checkoutdate || s.checkoutDate
    ).length;
  }, [assignedSubjects]);

  const checkInPercent = useMemo(() => {
    if (totalRecords === 0) return 0;
    return Math.round((checkedInCount / totalRecords) * 100);
  }, [checkedInCount, totalRecords]);

  const checkOutPercent = useMemo(() => {
    if (totalRecords === 0) return 0;
    return Math.round((checkedOutCount / totalRecords) * 100);
  }, [checkedOutCount, totalRecords]);

  const handleCheckInConfirm = (data: {
    checkinDate?: string;
    checkinTime?: string;
    checkoutDate?: string;
    checkoutTime?: string;
    remarks?: string;
  }) => {
    const isCheckOut = activeCheckInSubject && (activeCheckInSubject.subjectvisitstatus === "checked in" || activeCheckInSubject.checkindate || activeCheckInSubject.checkinDate);
    const userData = GetUserData();

    if (isCheckOut) {
      const payload = {
        visitcheckincheckoutid: Number(activeCheckInSubject?.visitcheckincheckoutid || 0),
        checkoutdate: data.checkoutDate,
        checkouttime: data.checkoutTime ? (data.checkoutTime.length === 5 ? `${data.checkoutTime}:00` : data.checkoutTime) : "",
        checkoutremarks: data.remarks || "",
        updatedby: Number(userData?.userid || 0)
      };

      checkOutSubject.mutate(payload, {
        onSuccess: (res: any) => {
          showToastMessage("success", res?.message || "Subject checked out successfully!");
          SelectAllAssignedSubjects.refetch();
          setCheckInOpen(false);
          setActiveCheckInSubject(null);
        },
        onError: (err: any) => {
          showToastMessage("error", err?.message || err || "Failed to check out subject!");
        }
      });
    } else {
      const payload = {
        visitsubjectassignmentid: Number(activeCheckInSubject?.visitsubjectassignmentid || 0),
        checkindate: data.checkinDate,
        checkintime: data.checkinTime ? (data.checkinTime.length === 5 ? `${data.checkinTime}:00` : data.checkinTime) : "",
        checkinremarks: data.remarks || "",
        createdby: Number(userData?.userid || 0)
      };

      checkInSubject.mutate(payload, {
        onSuccess: (res: any) => {
          showToastMessage("success", res?.message || "Subject checked in successfully!");
          SelectAllAssignedSubjects.refetch();
          setCheckInOpen(false);
          setActiveCheckInSubject(null);
        },
        onError: (err: any) => {
          showToastMessage("error", err?.message || err || "Failed to check in subject!");
        }
      });
    }
  };

  const handleStartVisitConfirm = () => {
    const userData = GetUserData();
    const payload = {
      visitid: Number(visitId),
      studyid: Number(studyid),
      startedby: Number(userData?.userid || 0)
    };

    insertAmbulatoryVisitStatusHistory.mutate(payload, {
      onSuccess: (res: any) => {
        showToastMessage("success", res?.message || "Visit started successfully!");
        setVisit((prev: any) => {
          if (!prev || !prev.visit || !prev.visit[0]) return prev;
          return {
            ...prev,
            visit: [
              {
                ...prev.visit[0],
                visitstatus: "started",
              },
            ],
          };
        });
        SelectdataAmbulatoryVisit.refetch();
        setStartVisitOpen(false);
      },
      onError: (err: any) => {
        showToastMessage("error", err?.message || err || "Failed to start visit!");
      }
    });
  };

  const handleCompleteVisitConfirm = () => {
    const userData = GetUserData();
    const payload = {
      visitid: Number(visitId),
      studyid: Number(studyid),
      completedby: Number(userData?.userid || 0)
    };

    insertAmbulatoryVisitStatusHistory.mutate(payload, {
      onSuccess: (res: any) => {
        showToastMessage("success", res?.message || "Visit completed successfully!");
        setVisit((prev: any) => {
          if (!prev || !prev.visit || !prev.visit[0]) return prev;
          return {
            ...prev,
            visit: [
              {
                ...prev.visit[0],
                visitstatus: "completed",
              },
            ],
          };
        });
        SelectdataAmbulatoryVisit.refetch();
        setCompleteVisitOpen(false);
      },
      onError: (err: any) => {
        showToastMessage("error", err?.message || err || "Failed to complete visit!");
      }
    });
  };

  if (SelectdataAmbulatoryVisit.isLoading) {
    return (
      <FullPageLoader/>
    );
  }

  if (!visit || !visitData) {
    return (
      <div className="page-content text-center py-5">
        <h5 className="text-muted">Visit not found.</h5>
        <Button color="primary" onClick={() => navigate('/study/detail/ambulatory')} className="mt-3">
          Back to List
        </Button>
      </div>
    );
  }

  return (
    <div className="page-content mx-0 px-0">
      <div className={styles.dashboardContainer}>
        <Container fluid>
          {/* Detail Header */}
          <div className={styles.detailHeader} style={{
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            boxShadow: "0 4px 16px rgba(15, 23, 42, 0.08)"
          }}>
            <div className={styles.detailHeaderRow} style={{ borderBottom: "1px solid #d9dee3", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div className="d-flex align-items-center gap-3">
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <h4 className="fw-bold mb-0 text-dark">
                      {(visitData.visitid ? `${String(visitData.visitnumber)}` : visitData.visitnumber) || "--"} 
                    </h4>
                    <Commonbadge status={visitData.visitstatus || "scheduled"} style={{ fontSize: "12px", padding: "4px 10px" }} />
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <CommonButton 
                  module={PERMISSION_MODULES.AMBULATORY_VISIT}
                  action={PERMISSION_ACTIONS.VIEW}
                  color="primary" 
                  outline
                  size="md" 
                  onClick={() => setAllActivitiesOpen(true)}
                >
                  <i className="ri-eye-line" /> View Activities
                </CommonButton>
                {!(visitData.visitstatus === "In Progress" || visitData.visitstatus === "started" || visitData.visitstatus === "completed") && (
                  <CommonButton 
                    module={PERMISSION_MODULES.AMBULATORY_VISIT}
                    action={PERMISSION_ACTIONS.START_VISIT}
                    color="primary" 
                    size="md" 
                    onClick={() => setStartVisitOpen(true)}
                  >
                    <i className="ri-play-fill" /> Start Visit
                  </CommonButton>
                )}
                {(visitData.visitstatus === "started" || visitData.visitstatus === "In Progress") && (
                  <CommonButton 
                    module={PERMISSION_MODULES.AMBULATORY_VISIT}
                    action={PERMISSION_ACTIONS.COMPLETE_VISIT}
                    color="primary" 
                    size="md" 
                    onClick={() => setCompleteVisitOpen(true)}
                  >
                    <i className="ri-checkbox-circle-line" /> Complete Visit
                  </CommonButton>
                )}
              </div>
            </div>

            {/* Meta Details Horizontal Grid */}
            <div className={styles.detailMetaGrid}>
              {/* Block 1: Visit Name & Progress */}
              <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                 <i className="ri-file-list-3-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                <div>
                  <div className={styles.metaLabel} style={{ fontSize: "11px", marginBottom: "2px" }}>Visit Name</div>
                  <div className={styles.metaValue} style={{ color: "#0f172a", fontSize: "14px", fontWeight: 700 }}>
                    <TruncatedText text={visitData.visitname || "--"} maxWidth="150px" />
                  </div>
                </div>
              </div>

              {/* Block 2: Timeline & Type */}
              <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                <i className="ri-road-map-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                <div>
                  <div className={styles.metaLabel} style={{ fontSize: "11px", marginBottom: "2px" }}>Visit Type</div>
                  <div className={styles.metaValue} style={{ color: "#0f172a", fontSize: "14px", fontWeight: 700 }}>
                    {visitData.visittypename || "--"}
                  </div>
                </div>
              </div>

              {/* Block 3: Scheduled On */}
              <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                <i className="ri-calendar-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                <div>
                  <div className={styles.metaLabel} style={{ fontSize: "11px", marginBottom: "2px" }}>Scheduled On</div>
                  <div className={styles.metaValue} style={{ color: "#0f172a", fontSize: "14px", fontWeight: 700 }}>
                    {visitData.scheduleddate ? formatDate(visitData.scheduleddate) : "--"}
                  </div>
                </div>
              </div>

              {/* Block 4: Visit Window */}
              <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                <i className="ri-time-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                <div>
                  <div className={styles.metaLabel} style={{ fontSize: "11px", marginBottom: "2px" }}>Visit Window</div>
                  <div className="d-flex align-items-center gap-1">
                    <span className={styles.metaValue} style={{ color: "#0f172a", fontSize: "14px", fontWeight: 700 }}>
                      {visitData.visitstartday !== undefined ? `Day ${visitData.visitstartday} - Day ${visitData.visitendday}` : "--"}
                    </span>
                    <i className="ri-information-line text-muted fs-14 cursor-pointer" id="windowInfo" />
                  </div>
                </div>
              </div>

              {/* Block 5: Subjects Progress */}
              <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                <i className="ri-group-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                <div>
                  <div className={styles.metaLabel} style={{ fontSize: "11px", marginBottom: "2px" }}>Assigned Subjects</div>
                  <div className={styles.metaValue} style={{ color: "#0f172a", fontSize: "14px", fontWeight: 700 }}>
                    {totalRecords}
                  </div>
                  <div className={styles.metaSubtext} style={{ fontSize: "11px", color: "#64748b" }}>
                    {completedSubjectsCount} Completed
                  </div>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div style={{ borderTop: "1px solid #e2e8f0", margin: "16px 0" }} />

            {/* Progress Bars Row */}
            <div className="d-flex align-items-center flex-wrap flex-md-nowrap px-1" style={{ gap: "24px" }}>
              {/* Check-In Progress */}
              <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ flex: "1 1 0%", minWidth: "280px" }}>
                <div style={{
                  width: "36px", height: "36px", borderRadius: "8px",
                  background: "#eff6ff", display: "flex",
                  alignItems: "center", justifyContent: "center", flexShrink: 0
                }}>
                  <i className="ri-login-box-line text-primary fs-18" />
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span style={{ fontSize: "11px", fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      Check-In Progress
                    </span>
                    <span style={{ fontSize: "12px", fontWeight: 700, color: "#0f172a" }}>
                      {checkedInCount}/{totalRecords} ({checkInPercent}%)
                    </span>
                  </div>
                  <Progress value={checkInPercent} color="primary" style={{ height: "6px", borderRadius: "3px", margin: 0, backgroundColor: "#cbd5e1" }} />
                </div>
              </div>

              {/* Center Divider for Desktop */}
              <div className="d-none d-md-block" style={{ width: "1px", height: "36px", backgroundColor: "#e2e8f0", flexShrink: 0 }} />

              {/* Check-Out Progress */}
              <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ flex: "1 1 0%", minWidth: "280px" }}>
                <div style={{
                  width: "36px", height: "36px", borderRadius: "8px",
                  background: "#f0fdf4", display: "flex",
                  alignItems: "center", justifyContent: "center", flexShrink: 0
                }}>
                  <i className="ri-logout-box-line text-success fs-18" />
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span style={{ fontSize: "11px", fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      Visit Completion Progress
                    </span>
                    <span style={{ fontSize: "12px", fontWeight: 700, color: "#0f172a" }}>
                      {checkedOutCount}/{totalRecords} ({checkOutPercent}%)
                    </span>
                  </div>
                  <Progress value={checkOutPercent} color="success" style={{ height: "6px", borderRadius: "3px", margin: 0, backgroundColor: "#cbd5e1" }} />
                </div>
              </div>
            </div>
          </div>

          {/* Premium Main/Sidebar Layout */}
          <Row className="g-4">
            {/* Left Column: Assigned Subjects Table */}
            <Col xs={12}>
              <Card className="border-0 shadow-sm box-shadow-0 rounded-4 overflow-hidden" style={{ background: "#ffffff", border: "1px solid #e2e8f0", boxShadow: "0 4px 16px rgba(15, 23, 42, 0.08)" }}>
                <div className="px-4 py-3 border-bottom d-flex align-items-center justify-content-between" style={{
                  background: "#ffffff"
                }}>
                  <div className="d-flex align-items-center gap-2">
                    <div className="p-2 rounded bg-primary-subtle text-primary d-flex align-items-center justify-content-center" style={{ width: "36px", height: "36px" }}>
                      <i className="ri-group-line fs-18" />
                    </div>
                    <div>
                      <h5 className="fw-bold mb-0 text-dark">Assigned Subjects</h5>
                      <span className="text-muted extra-small">Volunteers enrolled for this visit check-in</span>
                    </div>
                  </div>
                  <Badge color="primary" className="rounded-pill px-3 py-1 font-size-12">{totalRecords} Assigned</Badge>
                </div>

                <div className="p-2 border-bottom align-items-center justify-content-between gap-3 flex-wrap">
                  <div className="d-flex align-items-center gap-2 justify-content-between flex-wrap px-3 py-1">
                  <div style={{ width: "280px" }}>
                    <CommonSearch onSearch={onSearch} searchText={searchText} placeholder="Search by name or ID..." />
                  </div>
                  {visitData?.visitstatus !== "started" && visitData?.visitstatus !== "completed" && 
                  <CommonButton
                    module={PERMISSION_MODULES.AMBULATORY_VISIT}
                    action={PERMISSION_ACTIONS.ASSIGN_SUBJECTS}
                    color="primary"
                    outline
                    onClick={() => handleAssignSubjectsClick(visitData)}
                  >
                    <i className="ri-user-add-line fs-14" /> Assign Subjects
                  </CommonButton>}
                </div>

                <CardBody className="px-3 py-1">
                  <CommonTableIndexWise
                    columns={subjectColumns(navigate, (row) => {
                      setActiveCheckInSubject(row);
                      setCheckInOpen(true);
                    }, visitData?.visitstatus, hasPermission)}
                    data={assignedSubjects}
                    loading={SelectAllAssignedSubjects.isPending || SelectAllAssignedSubjects.isFetching}
                    pageSize={10}
                    currentPage={currentPage}
                    totalRecords={totalRecords}
                    onPageChange={(p) => setCurrentPage(p)}
                    hideAction={true}
                  />
                </CardBody>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Assign Subjects Modal */}
          <AssignSubjectsModal
            isOpen={assignOpen}
            onClose={() => {
              setAssignOpen(false);
              setVisitToAssign(null);
            }}
            visit={visitToAssign}
          />

          {/* View All Activities Modal */}
          <ViewActivitiesModal
            isOpen={allActivitiesOpen}
            onClose={() => setAllActivitiesOpen(false)}
            activities={activitiesData}
          />

          {/* Start Visit Confirmation Modal */}
          <StartVisitModal
            isOpen={startVisitOpen}
            onClose={() => setStartVisitOpen(false)}
            onConfirm={handleStartVisitConfirm}
            assignedCount={totalRecords}
            visitNumber={(visitData.visitid ? `${String(visitData.visitnumber)}` : visitData.visitnumber) || "--"}
            isPending={insertAmbulatoryVisitStatusHistory.isPending}
          />

          {/* Complete Visit Confirmation Modal */}
          <CompleteVisitModal
            isOpen={completeVisitOpen}
            onClose={() => setCompleteVisitOpen(false)}
            onConfirm={handleCompleteVisitConfirm}
            visitNumber={(visitData.visitid ? `${String(visitData.visitnumber)}` : visitData.visitnumber) || "--"}
            subjects={assignedSubjects}
            isPending={insertAmbulatoryVisitStatusHistory.isPending}
          />

          {/* Ambulatory Check-In Modal */}
          <AmbulatoryCheckInModal
            isOpen={checkInOpen}
            onClose={() => {
              setCheckInOpen(false);
              setActiveCheckInSubject(null);
            }}
            onConfirm={handleCheckInConfirm}
            subjectInfo={activeCheckInSubject ? `Subject: ${String(activeCheckInSubject.subjectno).padStart(2, "0")}` : ""}
            visitCode={visitData ? `Visit: ${visitData.visitnumber}` : ""}
            mode={activeCheckInSubject && (activeCheckInSubject.subjectvisitstatus === "checked in" || activeCheckInSubject.checkindate || activeCheckInSubject.checkinDate) ? "checkout" : "checkin"}
            isPending={checkInSubject.isPending || checkOutSubject.isPending}
            scheduledDate={visitData?.scheduleddate || ""}
            visitStartDay={visitData?.visitstartday}
            visitEndDay={visitData?.visitendday}
            checkinDate={activeCheckInSubject?.checkindate || activeCheckInSubject?.checkinDate || ""}
            checkinTime={activeCheckInSubject?.checkintime || activeCheckInSubject?.checkinTime || ""}
          />
        </Container>
      </div>
    </div>
  );
};

export default AmbulatoryVisitDetail;
